// Verde vocabulary pillar — themed, CEFR-tiered, dual-coded (emoji) word cards.
//
// This is the NEW richer vocabulary dataset that backs the `vocabulary`
// PocketBase collection and the Vocabulary browse view. It is distinct from the
// small legacy `VOCABULARY` array in ./index.js (shape { es, en, cat }) that the
// old nouns/adjectives/flashcards views still read — that one is re-exported
// below for backward compatibility.
//
// Words and their meanings are language facts; the theme grouping, CEFR tiering,
// emoji pairing, and every example sentence are authored originally for Verde.
//
// Each row is a tuple kept terse on purpose so the data reads like a table:
//   [ spanish, english, emoji, part_of_speech, gender, example_es, example_en ]
// gender is "" for anything that isn't a noun (the builder stores it as "none").

export { VOCABULARY } from "./index.js";

// Net-new, collision-free per-theme cards live in standalone vocab-<theme>.js
// modules and are folded in below via buildVocabulary().
import { EXTRA_THEME_BLOCKS } from "./vocab-expansion.js";

const THEMES = [
  // ---- Identity --------------------------------------------------------
  {
    theme: "identity", tier: "A1", rows: [
      ["el nombre", "name", "🪪", "noun", "m", "Mi nombre es Ana.", "My name is Ana."],
      ["el apellido", "surname", "🔤", "noun", "m", "¿Cuál es tu apellido?", "What is your surname?"],
      ["la edad", "age", "🎂", "noun", "f", "Tengo treinta años de edad.", "I am thirty years of age."],
      ["el país", "country", "🌍", "noun", "m", "España es un país bonito.", "Spain is a beautiful country."],
      ["la nacionalidad", "nationality", "🛂", "noun", "f", "Mi nacionalidad es noruega.", "My nationality is Norwegian."],
      ["la dirección", "address", "🏡", "noun", "f", "¿Me das tu dirección?", "Will you give me your address?"],
      ["el teléfono", "phone", "📞", "noun", "m", "No tengo tu teléfono.", "I don't have your phone number."],
      ["el correo electrónico", "email", "📧", "noun", "m", "Te mando un correo electrónico.", "I'll send you an email."],
      ["soltero", "single", "🙋", "adjective", "", "Mi hermano está soltero.", "My brother is single."],
      ["casado", "married", "💑", "adjective", "", "Están casados desde mayo.", "They have been married since May."],
    ],
  },
  {
    theme: "identity", tier: "A2", rows: [
      ["el documento", "document", "📄", "noun", "m", "Necesito firmar el documento.", "I need to sign the document."],
      ["la firma", "signature", "✍️", "noun", "f", "Falta tu firma aquí.", "Your signature is missing here."],
      ["el ciudadano", "citizen", "🧑", "noun", "m", "Es ciudadano español.", "He is a Spanish citizen."],
      ["la profesión", "profession", "💼", "noun", "f", "¿Cuál es tu profesión?", "What is your profession?"],
      ["el estado civil", "marital status", "💍", "phrase", "", "Indique su estado civil.", "State your marital status."],
      ["nacer", "to be born", "👶", "verb", "", "Nací en Stavanger.", "I was born in Stavanger."],
      ["jubilado", "retired", "🧓", "adjective", "", "Mi padre está jubilado.", "My father is retired."],
      ["el carné", "ID card", "🪪", "noun", "m", "Enseña tu carné, por favor.", "Show your ID card, please."],
      ["el sexo", "sex", "⚧️", "noun", "m", "El formulario pregunta el sexo.", "The form asks for sex."],
      ["mayor de edad", "of legal age", "🔞", "phrase", "", "Ya es mayor de edad.", "She is already of legal age."],
    ],
  },

  {
    theme: "identity", tier: "B1", rows: [
      ["el origen", "origin", "🌐", "noun", "m", "Sus orígenes son humildes.", "His origins are humble."],
      ["la identidad", "identity", "🆔", "noun", "f", "Protege tu identidad en línea.", "Protect your identity online."],
      ["el parentesco", "kinship", "🧬", "noun", "m", "No tenemos ningún parentesco.", "We have no family relationship."],
      ["la herencia", "heritage", "🏺", "noun", "f", "La herencia cultural es rica.", "The cultural heritage is rich."],
      ["pertenecer", "to belong", "🧩", "verb", "", "Pertenezco a este club.", "I belong to this club."],
      ["la ascendencia", "ancestry", "🌳", "noun", "f", "Tiene ascendencia italiana.", "She has Italian ancestry."],
      ["el rasgo", "trait", "🎭", "noun", "m", "La generosidad es su mejor rasgo.", "Generosity is his best trait."],
      ["la personalidad", "personality", "✨", "noun", "f", "Tiene una personalidad fuerte.", "She has a strong personality."],
      ["la costumbre", "custom", "🔄", "noun", "f", "Es una costumbre familiar.", "It's a family custom."],
      ["arraigado", "deep-rooted", "🌱", "adjective", "", "Es un valor muy arraigado.", "It's a deeply rooted value."],
    ],
  },

  // ---- Family ----------------------------------------------------------
  {
    theme: "family", tier: "A1", rows: [
      ["la familia", "family", "👪", "noun", "f", "Mi familia es grande.", "My family is big."],
      ["el padre", "father", "👨", "noun", "m", "Mi padre cocina muy bien.", "My father cooks very well."],
      ["la madre", "mother", "👩", "noun", "f", "Llamo a mi madre cada día.", "I call my mother every day."],
      ["el hijo", "son", "👦", "noun", "m", "Su hijo tiene cinco años.", "Her son is five years old."],
      ["la hija", "daughter", "👧", "noun", "f", "Mi hija estudia música.", "My daughter studies music."],
      ["el hermano", "brother", "🧑", "noun", "m", "Tengo un hermano menor.", "I have a younger brother."],
      ["la hermana", "sister", "👭", "noun", "f", "Mi hermana vive en Madrid.", "My sister lives in Madrid."],
      ["el abuelo", "grandfather", "👴", "noun", "m", "El abuelo cuenta historias.", "Grandfather tells stories."],
      ["la abuela", "grandmother", "👵", "noun", "f", "La abuela hace un pastel.", "Grandmother makes a cake."],
      ["el bebé", "baby", "👶", "noun", "m", "El bebé duerme ahora.", "The baby is sleeping now."],
    ],
  },
  {
    theme: "family", tier: "A2", rows: [
      ["el tío", "uncle", "🧔", "noun", "m", "Mi tío vive en México.", "My uncle lives in Mexico."],
      ["la tía", "aunt", "👩", "noun", "f", "Mi tía es médica.", "My aunt is a doctor."],
      ["el primo", "cousin", "🧑", "noun", "m", "Mi primo juega al fútbol.", "My cousin plays football."],
      ["el sobrino", "nephew", "👦", "noun", "m", "Cuido a mi sobrino los lunes.", "I look after my nephew on Mondays."],
      ["el nieto", "grandson", "👶", "noun", "m", "Su nieto acaba de nacer.", "Her grandson has just been born."],
      ["el esposo", "husband", "🤵", "noun", "m", "Su esposo trabaja fuera.", "Her husband works away."],
      ["la esposa", "wife", "👰", "noun", "f", "Mi esposa habla tres idiomas.", "My wife speaks three languages."],
      ["el suegro", "father-in-law", "👴", "noun", "m", "Mi suegro es muy amable.", "My father-in-law is very kind."],
      ["el cuñado", "brother-in-law", "🧔", "noun", "m", "Mi cuñado es ingeniero.", "My brother-in-law is an engineer."],
      ["el yerno", "son-in-law", "🤵", "noun", "m", "Su yerno cocina los domingos.", "Her son-in-law cooks on Sundays."],
    ],
  },

  {
    theme: "family", tier: "B1", rows: [
      ["el antepasado", "ancestor", "🪦", "noun", "m", "Sus antepasados eran marineros.", "His ancestors were sailors."],
      ["la convivencia", "coexistence", "🤝", "noun", "f", "La convivencia mejora con respeto.", "Living together improves with respect."],
      ["el vínculo", "bond", "🔗", "noun", "m", "Hay un vínculo fuerte entre ellos.", "There's a strong bond between them."],
      ["criar", "to raise", "🍼", "verb", "", "Crían a sus hijos con cariño.", "They raise their children with love."],
      ["la crianza", "upbringing", "👐", "noun", "f", "La crianza requiere paciencia.", "Upbringing requires patience."],
      ["adoptar", "to adopt", "🤱", "verb", "", "Decidieron adoptar un niño.", "They decided to adopt a child."],
      ["la madrina", "godmother", "👼", "noun", "f", "Mi madrina vive cerca.", "My godmother lives nearby."],
      ["heredar", "to inherit", "📜", "verb", "", "Heredó la casa de su abuela.", "He inherited his grandmother's house."],
      ["el pariente", "relative", "👪", "noun", "m", "Tengo parientes en Chile.", "I have relatives in Chile."],
      ["unido", "close-knit", "❤️", "adjective", "", "Somos una familia muy unida.", "We are a very close-knit family."],
    ],
  },

  // ---- House & Home ----------------------------------------------------
  {
    theme: "house", tier: "A1", rows: [
      ["la casa", "house", "🏠", "noun", "f", "Vivo en una casa pequeña.", "I live in a small house."],
      ["el piso", "flat", "🏢", "noun", "m", "Su piso está en el centro.", "Her flat is in the centre."],
      ["la cocina", "kitchen", "🍳", "noun", "f", "La cocina es muy luminosa.", "The kitchen is very bright."],
      ["el baño", "bathroom", "🚿", "noun", "m", "El baño está al fondo.", "The bathroom is at the back."],
      ["la cama", "bed", "🛏️", "noun", "f", "La cama es muy cómoda.", "The bed is very comfortable."],
      ["la puerta", "door", "🚪", "noun", "f", "Cierra la puerta, por favor.", "Close the door, please."],
      ["la ventana", "window", "🪟", "noun", "f", "Abro la ventana por la mañana.", "I open the window in the morning."],
      ["la mesa", "table", "🍽️", "noun", "f", "Pon los platos en la mesa.", "Put the plates on the table."],
      ["la silla", "chair", "🪑", "noun", "f", "Esta silla está rota.", "This chair is broken."],
      ["el sofá", "sofa", "🛋️", "noun", "m", "Leo en el sofá por la noche.", "I read on the sofa at night."],
    ],
  },
  {
    theme: "house", tier: "A2", rows: [
      ["el dormitorio", "bedroom", "🛌", "noun", "m", "El dormitorio da al jardín.", "The bedroom faces the garden."],
      ["el salón", "living room", "📺", "noun", "m", "Vemos la tele en el salón.", "We watch TV in the living room."],
      ["el pasillo", "hallway", "🚪", "noun", "m", "El pasillo es estrecho.", "The hallway is narrow."],
      ["la escalera", "stairs", "🪜", "noun", "f", "Subo por la escalera.", "I go up the stairs."],
      ["el techo", "ceiling", "🏚️", "noun", "m", "El techo es muy alto.", "The ceiling is very high."],
      ["el suelo", "floor", "🧱", "noun", "m", "El suelo es de madera.", "The floor is wooden."],
      ["el armario", "wardrobe", "🚪", "noun", "m", "Guardo la ropa en el armario.", "I keep my clothes in the wardrobe."],
      ["la nevera", "fridge", "🧊", "noun", "f", "La leche está en la nevera.", "The milk is in the fridge."],
      ["la lámpara", "lamp", "💡", "noun", "f", "Enciende la lámpara.", "Turn on the lamp."],
      ["el jardín", "garden", "🌷", "noun", "m", "Tomamos café en el jardín.", "We have coffee in the garden."],
    ],
  },

  {
    theme: "house", tier: "B1", rows: [
      ["el alquiler", "rent", "🔑", "noun", "m", "El alquiler sube cada año.", "The rent goes up every year."],
      ["la hipoteca", "mortgage", "🏦", "noun", "f", "Pagamos la hipoteca a treinta años.", "We pay the mortgage over thirty years."],
      ["el inquilino", "tenant", "🚪", "noun", "m", "El inquilino firma el contrato.", "The tenant signs the contract."],
      ["la mudanza", "move", "📦", "noun", "f", "La mudanza fue agotadora.", "The move was exhausting."],
      ["amueblado", "furnished", "🛋️", "adjective", "", "Alquilo un piso amueblado.", "I rent a furnished flat."],
      ["el electrodoméstico", "appliance", "🔌", "noun", "m", "Compré un electrodoméstico nuevo.", "I bought a new appliance."],
      ["la fachada", "façade", "🏛️", "noun", "f", "La fachada necesita pintura.", "The façade needs paint."],
      ["el trastero", "storage room", "🗄️", "noun", "m", "Guardo las cajas en el trastero.", "I keep the boxes in the storage room."],
      ["acogedor", "cosy", "🕯️", "adjective", "", "Su salón es muy acogedor.", "Her living room is very cosy."],
      ["la calefacción", "heating", "🔥", "noun", "f", "La calefacción no funciona.", "The heating isn't working."],
    ],
  },

  // ---- Food & Drink ----------------------------------------------------
  {
    theme: "food", tier: "A1", rows: [
      ["el agua", "water", "💧", "noun", "f", "Bebo mucha agua.", "I drink a lot of water."],
      ["el pan", "bread", "🍞", "noun", "m", "Compro pan fresco.", "I buy fresh bread."],
      ["la leche", "milk", "🥛", "noun", "f", "Tomo café con leche.", "I have coffee with milk."],
      ["el café", "coffee", "☕", "noun", "m", "El café está caliente.", "The coffee is hot."],
      ["la fruta", "fruit", "🍎", "noun", "f", "La fruta es muy sana.", "Fruit is very healthy."],
      ["la carne", "meat", "🥩", "noun", "f", "No como carne roja.", "I don't eat red meat."],
      ["el pescado", "fish", "🐟", "noun", "m", "El pescado está fresco.", "The fish is fresh."],
      ["el huevo", "egg", "🥚", "noun", "m", "Desayuno un huevo.", "I have an egg for breakfast."],
      ["el queso", "cheese", "🧀", "noun", "m", "Me gusta el queso fuerte.", "I like strong cheese."],
      ["la comida", "food", "🍽️", "noun", "f", "La comida está lista.", "The food is ready."],
    ],
  },
  {
    theme: "food", tier: "A2", rows: [
      ["el desayuno", "breakfast", "🥐", "noun", "m", "El desayuno es a las ocho.", "Breakfast is at eight."],
      ["el almuerzo", "lunch", "🍲", "noun", "m", "El almuerzo dura una hora.", "Lunch lasts an hour."],
      ["la cena", "dinner", "🍝", "noun", "f", "Preparo la cena temprano.", "I make dinner early."],
      ["la receta", "recipe", "📋", "noun", "f", "Sigo la receta paso a paso.", "I follow the recipe step by step."],
      ["el azúcar", "sugar", "🍬", "noun", "m", "No tomo azúcar en el té.", "I don't take sugar in my tea."],
      ["la sal", "salt", "🧂", "noun", "f", "Falta sal en la sopa.", "The soup needs salt."],
      ["el aceite", "oil", "🫒", "noun", "m", "Cocino con aceite de oliva.", "I cook with olive oil."],
      ["la verdura", "vegetable", "🥦", "noun", "f", "Como verdura cada día.", "I eat vegetables every day."],
      ["el postre", "dessert", "🍰", "noun", "m", "¿Quieres postre?", "Would you like dessert?"],
      ["la bebida", "drink", "🥤", "noun", "f", "¿Qué bebida prefieres?", "Which drink do you prefer?"],
    ],
  },

  {
    theme: "food", tier: "B1", rows: [
      ["el ingrediente", "ingredient", "🧄", "noun", "m", "Falta un ingrediente clave.", "A key ingredient is missing."],
      ["la cucharada", "spoonful", "🥄", "noun", "f", "Añade una cucharada de miel.", "Add a spoonful of honey."],
      ["hervir", "to boil", "♨️", "verb", "", "Hierve el agua dos minutos.", "Boil the water for two minutes."],
      ["freír", "to fry", "🍳", "verb", "", "Frío las patatas en aceite.", "I fry the potatoes in oil."],
      ["el sabor", "flavour", "👅", "noun", "m", "Este plato tiene mucho sabor.", "This dish has a lot of flavour."],
      ["soso", "bland", "😐", "adjective", "", "La sopa está sosa.", "The soup is bland."],
      ["picante", "spicy", "🌶️", "adjective", "", "Me gusta la comida picante.", "I like spicy food."],
      ["la merienda", "afternoon snack", "🍪", "noun", "f", "Los niños toman la merienda.", "The children have their afternoon snack."],
      ["caducar", "to expire", "📆", "verb", "", "La leche caduca mañana.", "The milk expires tomorrow."],
      ["el congelador", "freezer", "❄️", "noun", "m", "Guarda el pescado en el congelador.", "Keep the fish in the freezer."],
    ],
  },

  // ---- Body & Health ---------------------------------------------------
  {
    theme: "body-health", tier: "A1", rows: [
      ["el cuerpo", "body", "🧍", "noun", "m", "El cuerpo necesita descanso.", "The body needs rest."],
      ["la cabeza", "head", "🗣️", "noun", "f", "Me duele la cabeza.", "My head hurts."],
      ["la mano", "hand", "✋", "noun", "f", "Levanta la mano.", "Raise your hand."],
      ["el ojo", "eye", "👁️", "noun", "m", "Tiene un ojo morado.", "He has a black eye."],
      ["la boca", "mouth", "👄", "noun", "f", "Abre la boca.", "Open your mouth."],
      ["el pie", "foot", "🦶", "noun", "m", "Me duele el pie izquierdo.", "My left foot hurts."],
      ["el brazo", "arm", "💪", "noun", "m", "Se rompió el brazo.", "He broke his arm."],
      ["la pierna", "leg", "🦵", "noun", "f", "Estira la pierna.", "Stretch your leg."],
      ["el pelo", "hair", "💇", "noun", "m", "Tiene el pelo largo.", "She has long hair."],
      ["la nariz", "nose", "👃", "noun", "f", "Me pica la nariz.", "My nose is itchy."],
    ],
  },
  {
    theme: "body-health", tier: "A2", rows: [
      ["la salud", "health", "❤️‍🩹", "noun", "f", "Cuida tu salud.", "Take care of your health."],
      ["la enfermedad", "illness", "🤒", "noun", "f", "Es una enfermedad común.", "It is a common illness."],
      ["el dolor", "pain", "😣", "noun", "m", "Siento un dolor fuerte.", "I feel a strong pain."],
      ["la fiebre", "fever", "🌡️", "noun", "f", "El niño tiene fiebre.", "The child has a fever."],
      ["la pastilla", "pill", "💊", "noun", "f", "Tomo una pastilla al día.", "I take one pill a day."],
      ["el médico", "doctor", "🩺", "noun", "m", "Voy al médico mañana.", "I'm going to the doctor tomorrow."],
      ["el hospital", "hospital", "🏥", "noun", "m", "El hospital está cerca.", "The hospital is nearby."],
      ["la cita médica", "medical appointment", "📅", "phrase", "", "Tengo una cita médica el viernes.", "I have a medical appointment on Friday."],
      ["toser", "to cough", "😷", "verb", "", "No paro de toser.", "I can't stop coughing."],
      ["sano", "healthy", "🥗", "adjective", "", "Lleva una vida sana.", "She leads a healthy life."],
    ],
  },

  {
    theme: "body-health", tier: "B1", rows: [
      ["el síntoma", "symptom", "🤒", "noun", "m", "El síntoma principal es la tos.", "The main symptom is a cough."],
      ["la receta médica", "prescription", "📝", "phrase", "", "Necesito una receta médica.", "I need a prescription."],
      ["el tratamiento", "treatment", "💉", "noun", "m", "El tratamiento dura un mes.", "The treatment lasts a month."],
      ["la herida", "wound", "🩹", "noun", "f", "Limpia la herida con cuidado.", "Clean the wound carefully."],
      ["recuperarse", "to recover", "🌤️", "verb", "", "Se recupera poco a poco.", "He's recovering little by little."],
      ["el latido", "heartbeat", "💓", "noun", "m", "Siento el latido del corazón.", "I feel the heartbeat."],
      ["mareado", "dizzy", "😵", "adjective", "", "Me siento mareado al levantarme.", "I feel dizzy when I get up."],
      ["la cirugía", "surgery", "🏥", "noun", "f", "La cirugía salió bien.", "The surgery went well."],
      ["el bienestar", "wellbeing", "🧘", "noun", "m", "El deporte mejora el bienestar.", "Sport improves wellbeing."],
      ["estornudar", "to sneeze", "🤧", "verb", "", "No para de estornudar.", "He won't stop sneezing."],
    ],
  },

  // ---- Daily Routine ---------------------------------------------------
  {
    theme: "daily-routine", tier: "A1", rows: [
      ["despertarse", "to wake up", "⏰", "verb", "", "Me despierto a las siete.", "I wake up at seven."],
      ["levantarse", "to get up", "🛌", "verb", "", "Me levanto enseguida.", "I get up right away."],
      ["ducharse", "to shower", "🚿", "verb", "", "Me ducho por la mañana.", "I shower in the morning."],
      ["vestirse", "to get dressed", "👕", "verb", "", "Me visto rápido.", "I get dressed quickly."],
      ["desayunar", "to have breakfast", "🥣", "verb", "", "Desayuno en casa.", "I have breakfast at home."],
      ["la rutina", "routine", "🔁", "noun", "f", "Tengo una rutina fija.", "I have a fixed routine."],
      ["el horario", "schedule", "🗓️", "noun", "m", "Mi horario cambia cada semana.", "My schedule changes every week."],
      ["temprano", "early", "🌅", "adverb", "", "Me acuesto temprano.", "I go to bed early."],
      ["tarde", "late", "🌙", "adverb", "", "Llego tarde al trabajo.", "I arrive late to work."],
      ["cada día", "every day", "📆", "phrase", "", "Estudio español cada día.", "I study Spanish every day."],
    ],
  },
  {
    theme: "daily-routine", tier: "A2", rows: [
      ["acostarse", "to go to bed", "🛏️", "verb", "", "Me acuesto a medianoche.", "I go to bed at midnight."],
      ["peinarse", "to comb one's hair", "💇", "verb", "", "Me peino antes de salir.", "I comb my hair before leaving."],
      ["cepillarse", "to brush", "🪥", "verb", "", "Me cepillo los dientes.", "I brush my teeth."],
      ["lavarse", "to wash oneself", "🧼", "verb", "", "Me lavo las manos.", "I wash my hands."],
      ["descansar", "to rest", "🛋️", "verb", "", "Descanso un rato después de comer.", "I rest a while after eating."],
      ["las tareas", "chores", "🧹", "noun", "f", "Hago las tareas los sábados.", "I do the chores on Saturdays."],
      ["soler", "to usually do", "🔁", "verb", "", "Suelo correr por la tarde.", "I usually run in the afternoon."],
      ["ocupado", "busy", "📚", "adjective", "", "Hoy estoy muy ocupado.", "Today I am very busy."],
      ["libre", "free", "🆓", "adjective", "", "Tengo la tarde libre.", "I have the afternoon free."],
      ["mientras", "while", "⏳", "conjunction", "", "Cocino mientras escucho música.", "I cook while I listen to music."],
    ],
  },

  {
    theme: "daily-routine", tier: "B1", rows: [
      ["madrugar", "to get up early", "🌄", "verb", "", "Madrugo para ir al gimnasio.", "I get up early to go to the gym."],
      ["el madrugón", "early rise", "⏰", "noun", "m", "Vaya madrugón esta mañana.", "What an early start this morning."],
      ["trasnochar", "to stay up late", "🌃", "verb", "", "No me gusta trasnochar.", "I don't like staying up late."],
      ["la siesta", "nap", "😴", "noun", "f", "Echo una siesta corta.", "I take a short nap."],
      ["el quehacer", "task", "🧺", "noun", "m", "Tengo muchos quehaceres hoy.", "I have many tasks today."],
      ["aprovechar", "to make the most of", "☀️", "verb", "", "Aprovecho la mañana para estudiar.", "I make the most of the morning to study."],
      ["el ajetreo", "hustle and bustle", "🏃", "noun", "m", "El ajetreo diario me cansa.", "The daily hustle tires me out."],
      ["relajarse", "to relax", "🧖", "verb", "", "Me relajo con un baño caliente.", "I relax with a hot bath."],
      ["cotidiano", "everyday", "📅", "adjective", "", "Es parte de mi vida cotidiana.", "It's part of my everyday life."],
      ["puntual", "punctual", "⏱️", "adjective", "", "Siempre es muy puntual.", "He's always very punctual."],
    ],
  },

  // ---- Work & Study ----------------------------------------------------
  {
    theme: "work-study", tier: "A1", rows: [
      ["el trabajo", "work", "💼", "noun", "m", "Voy al trabajo en tren.", "I go to work by train."],
      ["la escuela", "school", "🏫", "noun", "f", "Los niños van a la escuela.", "The children go to school."],
      ["el profesor", "teacher", "👨‍🏫", "noun", "m", "El profesor explica bien.", "The teacher explains well."],
      ["el estudiante", "student", "🎓", "noun", "m", "Soy estudiante de medicina.", "I am a medical student."],
      ["la oficina", "office", "🏢", "noun", "f", "La oficina está cerrada.", "The office is closed."],
      ["el libro", "book", "📖", "noun", "m", "Leo un libro interesante.", "I'm reading an interesting book."],
      ["el ordenador", "computer", "💻", "noun", "m", "Trabajo con el ordenador.", "I work with the computer."],
      ["estudiar", "to study", "📚", "verb", "", "Estudio por la noche.", "I study at night."],
      ["aprender", "to learn", "🧠", "verb", "", "Quiero aprender español.", "I want to learn Spanish."],
      ["el examen", "exam", "📝", "noun", "m", "El examen es difícil.", "The exam is difficult."],
    ],
  },
  {
    theme: "work-study", tier: "A2", rows: [
      ["la empresa", "company", "🏢", "noun", "f", "Trabajo en una empresa grande.", "I work at a big company."],
      ["el jefe", "boss", "🧑‍💼", "noun", "m", "Mi jefe es muy justo.", "My boss is very fair."],
      ["el sueldo", "salary", "💶", "noun", "m", "El sueldo es bueno.", "The salary is good."],
      ["la reunión", "meeting", "📊", "noun", "f", "La reunión es a las diez.", "The meeting is at ten."],
      ["el horario laboral", "working hours", "🕘", "phrase", "", "Mi horario laboral es flexible.", "My working hours are flexible."],
      ["la carrera", "degree", "🎓", "noun", "f", "Estudié una carrera técnica.", "I studied a technical degree."],
      ["el proyecto", "project", "📁", "noun", "m", "El proyecto va bien.", "The project is going well."],
      ["contratar", "to hire", "🤝", "verb", "", "Van a contratar a dos personas.", "They are going to hire two people."],
      ["el compañero", "colleague", "👥", "noun", "m", "Mi compañero es de Perú.", "My colleague is from Peru."],
      ["la entrevista", "interview", "🗣️", "noun", "f", "Tengo una entrevista el lunes.", "I have an interview on Monday."],
    ],
  },

  {
    theme: "work-study", tier: "B1", rows: [
      ["el ascenso", "promotion", "📈", "noun", "m", "Le dieron un ascenso.", "They gave him a promotion."],
      ["la jornada", "workday", "🕘", "noun", "f", "Tengo una jornada larga.", "I have a long workday."],
      ["el plazo", "deadline", "⏳", "noun", "m", "El plazo termina el viernes.", "The deadline ends on Friday."],
      ["la beca", "scholarship", "🎓", "noun", "f", "Conseguí una beca.", "I got a scholarship."],
      ["desempeñar", "to carry out", "🛠️", "verb", "", "Desempeña un papel importante.", "He plays an important role."],
      ["el currículum", "CV", "📄", "noun", "m", "Actualizo mi currículum.", "I'm updating my CV."],
      ["la formación", "training", "📚", "noun", "f", "La empresa ofrece formación.", "The company offers training."],
      ["el despido", "dismissal", "📉", "noun", "m", "El despido fue una sorpresa.", "The dismissal was a surprise."],
      ["emprender", "to start a venture", "🚀", "verb", "", "Quiere emprender un negocio.", "She wants to start a business."],
      ["la plantilla", "staff", "👥", "noun", "f", "La plantilla creció este año.", "The staff grew this year."],
    ],
  },

  // ---- Travel ----------------------------------------------------------
  {
    theme: "travel", tier: "A1", rows: [
      ["el viaje", "trip", "🧳", "noun", "m", "El viaje fue largo.", "The trip was long."],
      ["el avión", "plane", "✈️", "noun", "m", "El avión sale a las seis.", "The plane leaves at six."],
      ["el tren", "train", "🚆", "noun", "m", "Cojo el tren cada mañana.", "I take the train every morning."],
      ["el coche", "car", "🚗", "noun", "m", "Vamos en coche.", "We're going by car."],
      ["el hotel", "hotel", "🏨", "noun", "m", "El hotel está en la playa.", "The hotel is on the beach."],
      ["el billete", "ticket", "🎫", "noun", "m", "Compro el billete por internet.", "I buy the ticket online."],
      ["la maleta", "suitcase", "🧳", "noun", "f", "Hago la maleta esta noche.", "I'll pack the suitcase tonight."],
      ["el aeropuerto", "airport", "🛫", "noun", "m", "Llego al aeropuerto temprano.", "I arrive at the airport early."],
      ["viajar", "to travel", "🌍", "verb", "", "Me encanta viajar solo.", "I love travelling alone."],
      ["el mapa", "map", "🗺️", "noun", "m", "Miro el mapa de la ciudad.", "I look at the city map."],
    ],
  },
  {
    theme: "travel", tier: "A2", rows: [
      ["el pasaporte", "passport", "🛂", "noun", "m", "Mi pasaporte caduca pronto.", "My passport expires soon."],
      ["la frontera", "border", "🚧", "noun", "f", "Cruzamos la frontera de noche.", "We cross the border at night."],
      ["la estación", "station", "🚉", "noun", "f", "La estación está lejos.", "The station is far away."],
      ["el equipaje", "luggage", "🧳", "noun", "m", "Facturo el equipaje.", "I check in the luggage."],
      ["el destino", "destination", "📍", "noun", "m", "Nuestro destino es Sevilla.", "Our destination is Seville."],
      ["reservar", "to book", "📅", "verb", "", "Reservo una habitación doble.", "I book a double room."],
      ["el vuelo", "flight", "🛬", "noun", "m", "El vuelo tiene retraso.", "The flight is delayed."],
      ["la llegada", "arrival", "🛬", "noun", "f", "La llegada es a las nueve.", "Arrival is at nine."],
      ["la salida", "departure", "🚪", "noun", "f", "La salida está a la derecha.", "The exit is on the right."],
      ["el turista", "tourist", "📸", "noun", "m", "El turista saca fotos.", "The tourist takes photos."],
    ],
  },

  {
    theme: "travel", tier: "B1", rows: [
      ["el alojamiento", "accommodation", "🏨", "noun", "m", "El alojamiento incluye desayuno.", "The accommodation includes breakfast."],
      ["la escala", "stopover", "🛑", "noun", "f", "El vuelo tiene una escala.", "The flight has a stopover."],
      ["el itinerario", "itinerary", "🗺️", "noun", "m", "Planeo el itinerario con cuidado.", "I plan the itinerary carefully."],
      ["el seguro de viaje", "travel insurance", "🛡️", "phrase", "", "Contrato un seguro de viaje.", "I take out travel insurance."],
      ["embarcar", "to board", "🛫", "verb", "", "Embarcamos en media hora.", "We board in half an hour."],
      ["la aduana", "customs", "🛂", "noun", "f", "Pasamos por la aduana.", "We go through customs."],
      ["el trayecto", "journey", "🚌", "noun", "m", "El trayecto dura tres horas.", "The journey takes three hours."],
      ["alojarse", "to stay", "🛏️", "verb", "", "Nos alojamos en un hostal.", "We stay in a guesthouse."],
      ["la brújula", "compass", "🧭", "noun", "f", "Llevo una brújula en la mochila.", "I carry a compass in my backpack."],
      ["la mochila", "backpack", "🎒", "noun", "f", "Preparo la mochila para el viaje.", "I pack the backpack for the trip."],
    ],
  },

  // ---- Time & Weather --------------------------------------------------
  {
    theme: "time-weather", tier: "A1", rows: [
      ["el tiempo", "weather", "🌤️", "noun", "m", "Hoy hace buen tiempo.", "The weather is nice today."],
      ["el sol", "sun", "☀️", "noun", "m", "Hace sol esta mañana.", "It's sunny this morning."],
      ["la lluvia", "rain", "🌧️", "noun", "f", "La lluvia no para.", "The rain won't stop."],
      ["el viento", "wind", "💨", "noun", "m", "Hace mucho viento.", "It's very windy."],
      ["la nieve", "snow", "❄️", "noun", "f", "La nieve cubre el campo.", "The snow covers the field."],
      ["el día", "day", "📆", "noun", "m", "Es un día precioso.", "It's a lovely day."],
      ["la noche", "night", "🌙", "noun", "f", "La noche es fría.", "The night is cold."],
      ["hoy", "today", "📅", "adverb", "", "Hoy no trabajo.", "I'm not working today."],
      ["mañana", "tomorrow", "🌅", "adverb", "", "Mañana vamos al cine.", "Tomorrow we're going to the cinema."],
      ["hace calor", "it's hot", "🥵", "phrase", "", "En agosto hace calor.", "In August it's hot."],
    ],
  },
  {
    theme: "time-weather", tier: "A2", rows: [
      ["la temperatura", "temperature", "🌡️", "noun", "f", "La temperatura baja por la noche.", "The temperature drops at night."],
      ["la nube", "cloud", "☁️", "noun", "f", "Hay una nube negra.", "There's a black cloud."],
      ["la tormenta", "storm", "⛈️", "noun", "f", "Viene una tormenta fuerte.", "A strong storm is coming."],
      ["el grado", "degree", "🔆", "noun", "m", "Estamos a treinta grados.", "It's thirty degrees."],
      ["el pronóstico", "forecast", "📰", "noun", "m", "El pronóstico anuncia lluvia.", "The forecast predicts rain."],
      ["despejado", "clear", "🌞", "adjective", "", "El cielo está despejado.", "The sky is clear."],
      ["húmedo", "humid", "💦", "adjective", "", "El clima es húmedo aquí.", "The climate is humid here."],
      ["la estación del año", "season", "🍂", "phrase", "", "El otoño es mi estación del año favorita.", "Autumn is my favourite season."],
      ["el amanecer", "dawn", "🌄", "noun", "m", "Salimos al amanecer.", "We leave at dawn."],
      ["hace frío", "it's cold", "🥶", "phrase", "", "En invierno hace frío.", "In winter it's cold."],
    ],
  },

  {
    theme: "time-weather", tier: "B1", rows: [
      ["el chubasco", "shower", "🌦️", "noun", "m", "Caen chubascos por la tarde.", "Showers fall in the afternoon."],
      ["la niebla", "fog", "🌫️", "noun", "f", "Hay niebla en la carretera.", "There's fog on the road."],
      ["el granizo", "hail", "🌨️", "noun", "m", "El granizo dañó los coches.", "The hail damaged the cars."],
      ["soleado", "sunny", "🌞", "adjective", "", "Mañana será un día soleado.", "Tomorrow will be a sunny day."],
      ["nublado", "cloudy", "☁️", "adjective", "", "El cielo está nublado.", "The sky is cloudy."],
      ["la sequía", "drought", "🏜️", "noun", "f", "La sequía afecta al campo.", "The drought affects the countryside."],
      ["el relámpago", "lightning", "⚡", "noun", "m", "Vimos un relámpago lejano.", "We saw distant lightning."],
      ["helar", "to freeze", "🧊", "verb", "", "Anoche heló en el valle.", "Last night it froze in the valley."],
      ["el atardecer", "dusk", "🌇", "noun", "m", "Paseamos al atardecer.", "We walk at dusk."],
      ["templado", "mild", "🌡️", "adjective", "", "El clima es templado en otoño.", "The climate is mild in autumn."],
    ],
  },

  // ---- Shopping --------------------------------------------------------
  {
    theme: "shopping", tier: "A1", rows: [
      ["la tienda", "shop", "🏪", "noun", "f", "La tienda abre a las nueve.", "The shop opens at nine."],
      ["el dinero", "money", "💶", "noun", "m", "No llevo dinero suelto.", "I don't have any loose change."],
      ["el precio", "price", "🏷️", "noun", "m", "El precio es muy alto.", "The price is very high."],
      ["comprar", "to buy", "🛒", "verb", "", "Quiero comprar zapatos.", "I want to buy shoes."],
      ["pagar", "to pay", "💳", "verb", "", "Pago con tarjeta.", "I pay by card."],
      ["caro", "expensive", "💸", "adjective", "", "Este reloj es muy caro.", "This watch is very expensive."],
      ["barato", "cheap", "🪙", "adjective", "", "La fruta está barata hoy.", "Fruit is cheap today."],
      ["el euro", "euro", "💶", "noun", "m", "Cuesta cinco euros.", "It costs five euros."],
      ["la ropa", "clothes", "👕", "noun", "f", "Compro ropa de invierno.", "I buy winter clothes."],
      ["el mercado", "market", "🧺", "noun", "m", "Voy al mercado los sábados.", "I go to the market on Saturdays."],
    ],
  },
  {
    theme: "shopping", tier: "A2", rows: [
      ["la oferta", "offer", "🏷️", "noun", "f", "Hay una oferta esta semana.", "There's an offer this week."],
      ["el descuento", "discount", "🔖", "noun", "m", "Me hacen un descuento.", "They give me a discount."],
      ["la talla", "size", "📏", "noun", "f", "¿Qué talla usas?", "What size do you wear?"],
      ["el recibo", "receipt", "🧾", "noun", "m", "Guarda el recibo.", "Keep the receipt."],
      ["la caja", "checkout", "💰", "noun", "f", "Pague en la caja, por favor.", "Pay at the checkout, please."],
      ["gastar", "to spend", "💸", "verb", "", "Gasto poco en ropa.", "I spend little on clothes."],
      ["devolver", "to return", "↩️", "verb", "", "Quiero devolver esta camisa.", "I want to return this shirt."],
      ["el cliente", "customer", "🧑", "noun", "m", "El cliente siempre pregunta.", "The customer always asks."],
      ["probarse", "to try on", "👗", "verb", "", "Me pruebo el vestido.", "I try on the dress."],
      ["el escaparate", "shop window", "🪟", "noun", "m", "Miro el escaparate.", "I look at the shop window."],
    ],
  },

  {
    theme: "shopping", tier: "B1", rows: [
      ["el presupuesto", "budget", "💰", "noun", "m", "Tengo un presupuesto ajustado.", "I have a tight budget."],
      ["la factura", "invoice", "🧾", "noun", "f", "Pido la factura, por favor.", "I ask for the invoice, please."],
      ["el reembolso", "refund", "💵", "noun", "m", "Solicito un reembolso.", "I request a refund."],
      ["regatear", "to haggle", "🤝", "verb", "", "Es normal regatear en el mercado.", "It's normal to haggle in the market."],
      ["agotado", "sold out", "🚫", "adjective", "", "El producto está agotado.", "The product is sold out."],
      ["la garantía", "warranty", "📑", "noun", "f", "La garantía dura dos años.", "The warranty lasts two years."],
      ["el comercio", "business", "🏬", "noun", "m", "El comercio local resiste.", "Local business holds on."],
      ["asequible", "affordable", "🪙", "adjective", "", "Es un precio asequible.", "It's an affordable price."],
      ["el envío", "delivery", "📦", "noun", "m", "El envío es gratuito.", "Delivery is free."],
      ["la marca", "brand", "🏷️", "noun", "f", "Prefiero esta marca.", "I prefer this brand."],
    ],
  },

  // ---- Town & Directions -----------------------------------------------
  {
    theme: "town", tier: "A1", rows: [
      ["la ciudad", "city", "🏙️", "noun", "f", "Vivo en una ciudad grande.", "I live in a big city."],
      ["el pueblo", "town", "🏘️", "noun", "m", "Mi pueblo es muy tranquilo.", "My town is very quiet."],
      ["la calle", "street", "🛣️", "noun", "f", "La calle está vacía.", "The street is empty."],
      ["la plaza", "square", "⛲", "noun", "f", "Quedamos en la plaza.", "Let's meet in the square."],
      ["el parque", "park", "🌳", "noun", "m", "Paseo por el parque.", "I walk through the park."],
      ["el banco", "bank", "🏦", "noun", "m", "El banco cierra a las dos.", "The bank closes at two."],
      ["el restaurante", "restaurant", "🍴", "noun", "m", "El restaurante es nuevo.", "The restaurant is new."],
      ["la iglesia", "church", "⛪", "noun", "f", "La iglesia es del siglo XV.", "The church is from the 15th century."],
      ["el museo", "museum", "🏛️", "noun", "m", "El museo abre el domingo.", "The museum opens on Sunday."],
      ["el supermercado", "supermarket", "🛒", "noun", "m", "Voy al supermercado a pie.", "I walk to the supermarket."],
    ],
  },
  {
    theme: "town", tier: "A2", rows: [
      ["la esquina", "corner", "📐", "noun", "f", "Gira en la esquina.", "Turn at the corner."],
      ["el semáforo", "traffic light", "🚦", "noun", "m", "Para en el semáforo.", "Stop at the traffic light."],
      ["el puente", "bridge", "🌉", "noun", "m", "Cruza el puente.", "Cross the bridge."],
      ["la acera", "pavement", "🚶", "noun", "f", "Camina por la acera.", "Walk on the pavement."],
      ["el ayuntamiento", "town hall", "🏛️", "noun", "m", "El ayuntamiento está en el centro.", "The town hall is in the centre."],
      ["girar", "to turn", "↩️", "verb", "", "Gira a la izquierda.", "Turn left."],
      ["seguir recto", "to go straight", "⬆️", "phrase", "", "Sigue recto dos calles.", "Go straight on for two streets."],
      ["la parada", "stop", "🚏", "noun", "f", "La parada está aquí mismo.", "The stop is right here."],
      ["el barrio", "neighbourhood", "🏘️", "noun", "m", "Mi barrio es muy seguro.", "My neighbourhood is very safe."],
      ["cruzar", "to cross", "🚸", "verb", "", "Cruza con cuidado.", "Cross carefully."],
    ],
  },

  {
    theme: "town", tier: "B1", rows: [
      ["el casco antiguo", "old town", "🏰", "phrase", "", "El casco antiguo es precioso.", "The old town is beautiful."],
      ["las afueras", "outskirts", "🌆", "noun", "f", "Viven en las afueras.", "They live on the outskirts."],
      ["el peatón", "pedestrian", "🚶", "noun", "m", "El peatón tiene prioridad.", "The pedestrian has priority."],
      ["la rotonda", "roundabout", "🔄", "noun", "f", "Toma la segunda salida de la rotonda.", "Take the second exit at the roundabout."],
      ["el alumbrado", "street lighting", "💡", "noun", "m", "El alumbrado es nuevo.", "The street lighting is new."],
      ["concurrido", "crowded", "👥", "adjective", "", "Es una zona muy concurrida.", "It's a very crowded area."],
      ["el quiosco", "kiosk", "🗞️", "noun", "m", "Compro el periódico en el quiosco.", "I buy the newspaper at the kiosk."],
      ["la urbanización", "housing estate", "🏘️", "noun", "f", "Vivo en una urbanización tranquila.", "I live in a quiet housing estate."],
      ["peatonal", "pedestrian", "🚸", "adjective", "", "Es una calle peatonal.", "It's a pedestrian street."],
      ["la comisaría", "police station", "🚓", "noun", "f", "La comisaría está en la plaza.", "The police station is on the square."],
    ],
  },

  // ---- Leisure ---------------------------------------------------------
  {
    theme: "leisure", tier: "A1", rows: [
      ["la música", "music", "🎵", "noun", "f", "Escucho música clásica.", "I listen to classical music."],
      ["el cine", "cinema", "🎬", "noun", "m", "Vamos al cine el viernes.", "We're going to the cinema on Friday."],
      ["el deporte", "sport", "🏃", "noun", "m", "Hago deporte tres veces por semana.", "I do sport three times a week."],
      ["la fiesta", "party", "🎉", "noun", "f", "La fiesta es en mi casa.", "The party is at my house."],
      ["el fútbol", "football", "⚽", "noun", "m", "El fútbol es muy popular.", "Football is very popular."],
      ["bailar", "to dance", "💃", "verb", "", "Me gusta bailar salsa.", "I like to dance salsa."],
      ["cantar", "to sing", "🎤", "verb", "", "Canta muy bien.", "She sings very well."],
      ["leer", "to read", "📖", "verb", "", "Leo antes de dormir.", "I read before sleeping."],
      ["jugar", "to play", "🎮", "verb", "", "Juego con mis amigos.", "I play with my friends."],
      ["nadar", "to swim", "🏊", "verb", "", "Nado en el mar en verano.", "I swim in the sea in summer."],
    ],
  },
  {
    theme: "leisure", tier: "A2", rows: [
      ["el concierto", "concert", "🎶", "noun", "m", "El concierto empieza tarde.", "The concert starts late."],
      ["la entrada", "ticket", "🎟️", "noun", "f", "La entrada cuesta veinte euros.", "The ticket costs twenty euros."],
      ["el equipo", "team", "👕", "noun", "m", "Mi equipo ganó el partido.", "My team won the match."],
      ["el aficionado", "fan", "📣", "noun", "m", "Es un aficionado del baloncesto.", "He is a basketball fan."],
      ["la afición", "hobby", "🎨", "noun", "f", "Mi afición es la fotografía.", "My hobby is photography."],
      ["el espectáculo", "show", "🎭", "noun", "m", "El espectáculo fue increíble.", "The show was amazing."],
      ["quedar", "to meet up", "🤝", "verb", "", "Quedamos a las siete.", "Let's meet up at seven."],
      ["disfrutar", "to enjoy", "😄", "verb", "", "Disfruto de los fines de semana.", "I enjoy the weekends."],
      ["el ocio", "leisure", "🛋️", "noun", "m", "Dedico poco tiempo al ocio.", "I devote little time to leisure."],
      ["pasarlo bien", "to have a good time", "🥳", "phrase", "", "Lo pasamos muy bien anoche.", "We had a great time last night."],
    ],
  },

  {
    theme: "leisure", tier: "B1", rows: [
      ["el pasatiempo", "pastime", "🎲", "noun", "m", "Coleccionar sellos es su pasatiempo.", "Collecting stamps is his pastime."],
      ["el estreno", "premiere", "🎬", "noun", "m", "Vamos al estreno esta noche.", "We're going to the premiere tonight."],
      ["ensayar", "to rehearse", "🎻", "verb", "", "Ensayamos los martes.", "We rehearse on Tuesdays."],
      ["la taquilla", "box office", "🎟️", "noun", "f", "Compro las entradas en la taquilla.", "I buy the tickets at the box office."],
      ["apostar", "to bet", "🎰", "verb", "", "No me gusta apostar dinero.", "I don't like betting money."],
      ["el ajedrez", "chess", "♟️", "noun", "m", "Juego al ajedrez con mi abuelo.", "I play chess with my grandfather."],
      ["el senderismo", "hiking", "🥾", "noun", "m", "El senderismo me relaja.", "Hiking relaxes me."],
      ["coleccionar", "to collect", "🗂️", "verb", "", "Colecciona monedas antiguas.", "He collects old coins."],
      ["el espectador", "spectator", "👀", "noun", "m", "Los espectadores aplaudieron.", "The spectators applauded."],
      ["entretenido", "entertaining", "😄", "adjective", "", "La película es muy entretenida.", "The film is very entertaining."],
    ],
  },

  // ---- Feelings --------------------------------------------------------
  {
    theme: "feelings", tier: "A1", rows: [
      ["feliz", "happy", "😊", "adjective", "", "Estoy muy feliz hoy.", "I'm very happy today."],
      ["triste", "sad", "😢", "adjective", "", "Se siente triste.", "He feels sad."],
      ["cansado", "tired", "😫", "adjective", "", "Estoy cansado del viaje.", "I'm tired from the trip."],
      ["contento", "glad", "🙂", "adjective", "", "Estamos contentos con la casa.", "We're glad with the house."],
      ["enfadado", "angry", "😠", "adjective", "", "Está enfadado conmigo.", "He's angry with me."],
      ["nervioso", "nervous", "😬", "adjective", "", "Estoy nervioso por el examen.", "I'm nervous about the exam."],
      ["el miedo", "fear", "😨", "noun", "m", "Tengo miedo a la oscuridad.", "I'm afraid of the dark."],
      ["la alegría", "joy", "😁", "noun", "f", "Sentí una gran alegría.", "I felt great joy."],
      ["aburrido", "bored", "🥱", "adjective", "", "Estoy aburrido en casa.", "I'm bored at home."],
      ["tener hambre", "to be hungry", "🍽️", "phrase", "", "Tengo mucha hambre.", "I'm very hungry."],
    ],
  },
  {
    theme: "feelings", tier: "A2", rows: [
      ["emocionado", "excited", "🤩", "adjective", "", "Estoy emocionado por el viaje.", "I'm excited about the trip."],
      ["preocupado", "worried", "😟", "adjective", "", "Está preocupada por su hijo.", "She's worried about her son."],
      ["sorprendido", "surprised", "😲", "adjective", "", "Me quedé sorprendido.", "I was surprised."],
      ["orgulloso", "proud", "😌", "adjective", "", "Estoy orgulloso de ti.", "I'm proud of you."],
      ["avergonzado", "embarrassed", "😳", "adjective", "", "Se sintió avergonzado.", "He felt embarrassed."],
      ["los celos", "jealousy", "😒", "noun", "m", "Los celos no ayudan.", "Jealousy doesn't help."],
      ["el ánimo", "mood", "💪", "noun", "m", "Hoy tengo buen ánimo.", "Today I'm in good spirits."],
      ["sentirse", "to feel", "🫀", "verb", "", "Me siento mucho mejor.", "I feel much better."],
      ["echar de menos", "to miss", "💔", "phrase", "", "Echo de menos a mi familia.", "I miss my family."],
      ["estar de buen humor", "to be in a good mood", "😄", "phrase", "", "Hoy estoy de buen humor.", "Today I'm in a good mood."],
    ],
  },

  {
    theme: "feelings", tier: "B1", rows: [
      ["la angustia", "anguish", "😰", "noun", "f", "Sentí una angustia profunda.", "I felt deep anguish."],
      ["el alivio", "relief", "😮‍💨", "noun", "m", "Qué alivio terminar el examen.", "What a relief to finish the exam."],
      ["asustado", "scared", "😱", "adjective", "", "El niño está asustado.", "The child is scared."],
      ["la confianza", "trust", "🤝", "noun", "f", "Tengo plena confianza en ti.", "I have full trust in you."],
      ["animarse", "to cheer up", "🙂", "verb", "", "Anímate, todo irá bien.", "Cheer up, everything will be fine."],
      ["la decepción", "disappointment", "💧", "noun", "f", "Fue una gran decepción.", "It was a big disappointment."],
      ["agobiado", "overwhelmed", "😩", "adjective", "", "Estoy agobiado con el trabajo.", "I'm overwhelmed with work."],
      ["el cariño", "affection", "🥰", "noun", "m", "Le tengo mucho cariño.", "I'm very fond of him."],
      ["enfadarse", "to get angry", "😤", "verb", "", "Se enfada por todo.", "He gets angry about everything."],
      ["esperanzado", "hopeful", "🌈", "adjective", "", "Estoy esperanzado con el futuro.", "I'm hopeful about the future."],
    ],
  },

  // ---- Nature ----------------------------------------------------------
  {
    theme: "nature", tier: "A1", rows: [
      ["el árbol", "tree", "🌳", "noun", "m", "El árbol da mucha sombra.", "The tree gives a lot of shade."],
      ["la flor", "flower", "🌸", "noun", "f", "La flor huele bien.", "The flower smells nice."],
      ["el mar", "sea", "🌊", "noun", "m", "El mar está tranquilo.", "The sea is calm."],
      ["la montaña", "mountain", "⛰️", "noun", "f", "Subimos la montaña.", "We climb the mountain."],
      ["el río", "river", "🏞️", "noun", "m", "El río pasa por el pueblo.", "The river runs through the town."],
      ["el animal", "animal", "🐾", "noun", "m", "Es un animal salvaje.", "It's a wild animal."],
      ["el perro", "dog", "🐕", "noun", "m", "El perro juega en el jardín.", "The dog plays in the garden."],
      ["el gato", "cat", "🐈", "noun", "m", "El gato duerme todo el día.", "The cat sleeps all day."],
      ["el cielo", "sky", "🌌", "noun", "m", "El cielo está azul.", "The sky is blue."],
      ["la playa", "beach", "🏖️", "noun", "f", "Vamos a la playa.", "We're going to the beach."],
    ],
  },
  {
    theme: "nature", tier: "A2", rows: [
      ["el bosque", "forest", "🌲", "noun", "m", "El bosque es muy denso.", "The forest is very dense."],
      ["la hoja", "leaf", "🍃", "noun", "f", "Cae una hoja del árbol.", "A leaf falls from the tree."],
      ["la planta", "plant", "🪴", "noun", "f", "Riego la planta cada día.", "I water the plant every day."],
      ["el campo", "countryside", "🌾", "noun", "m", "Pasamos el día en el campo.", "We spend the day in the countryside."],
      ["la isla", "island", "🏝️", "noun", "f", "La isla es muy pequeña.", "The island is very small."],
      ["el pájaro", "bird", "🐦", "noun", "m", "El pájaro canta al amanecer.", "The bird sings at dawn."],
      ["el medio ambiente", "environment", "🌍", "phrase", "", "Hay que cuidar el medio ambiente.", "We must take care of the environment."],
      ["contaminar", "to pollute", "🏭", "verb", "", "Los coches contaminan mucho.", "Cars pollute a lot."],
      ["el paisaje", "landscape", "🖼️", "noun", "m", "El paisaje es impresionante.", "The landscape is stunning."],
      ["salvaje", "wild", "🐺", "adjective", "", "Es una zona salvaje.", "It's a wild area."],
    ],
  },

  {
    theme: "nature", tier: "B1", rows: [
      ["el medio rural", "rural environment", "🚜", "phrase", "", "Crecí en el medio rural.", "I grew up in the countryside."],
      ["la cordillera", "mountain range", "🏔️", "noun", "f", "La cordillera separa los países.", "The mountain range separates the countries."],
      ["el arroyo", "stream", "🏞️", "noun", "m", "El arroyo baja de la montaña.", "The stream comes down from the mountain."],
      ["la fauna", "wildlife", "🦌", "noun", "f", "La fauna local es variada.", "The local wildlife is varied."],
      ["la flora", "flora", "🌿", "noun", "f", "La flora del bosque es densa.", "The forest flora is dense."],
      ["sembrar", "to sow", "🌱", "verb", "", "Sembramos trigo en primavera.", "We sow wheat in spring."],
      ["el cultivo", "crop", "🌾", "noun", "m", "El cultivo necesita agua.", "The crop needs water."],
      ["la cumbre", "summit", "🗻", "noun", "f", "Llegamos a la cumbre al mediodía.", "We reach the summit at midday."],
      ["proteger", "to protect", "🛡️", "verb", "", "Debemos proteger la naturaleza.", "We must protect nature."],
      ["la especie", "species", "🐾", "noun", "f", "Es una especie en peligro.", "It's an endangered species."],
    ],
  },

  // ---- Describing Things -----------------------------------------------
  {
    theme: "describing", tier: "A1", rows: [
      ["grande", "big", "🐘", "adjective", "", "Es una casa grande.", "It's a big house."],
      ["pequeño", "small", "🐁", "adjective", "", "Tengo un coche pequeño.", "I have a small car."],
      ["nuevo", "new", "✨", "adjective", "", "Es mi teléfono nuevo.", "It's my new phone."],
      ["viejo", "old", "📦", "adjective", "", "Es un edificio viejo.", "It's an old building."],
      ["bueno", "good", "👍", "adjective", "", "Es un buen libro.", "It's a good book."],
      ["malo", "bad", "👎", "adjective", "", "Hace mal tiempo.", "The weather is bad."],
      ["bonito", "pretty", "🌷", "adjective", "", "Qué vestido más bonito.", "What a pretty dress."],
      ["feo", "ugly", "🙈", "adjective", "", "El cuadro es un poco feo.", "The painting is a bit ugly."],
      ["largo", "long", "📏", "adjective", "", "Es un camino largo.", "It's a long road."],
      ["corto", "short", "✂️", "adjective", "", "El vídeo es corto.", "The video is short."],
    ],
  },
  {
    theme: "describing", tier: "A2", rows: [
      ["amable", "kind", "🤗", "adjective", "", "El camarero es muy amable.", "The waiter is very kind."],
      ["inteligente", "intelligent", "🧠", "adjective", "", "Es una niña inteligente.", "She's an intelligent girl."],
      ["fuerte", "strong", "💪", "adjective", "", "El café está muy fuerte.", "The coffee is very strong."],
      ["débil", "weak", "🥀", "adjective", "", "La señal es débil.", "The signal is weak."],
      ["rápido", "fast", "⚡", "adjective", "", "Es un tren rápido.", "It's a fast train."],
      ["lento", "slow", "🐢", "adjective", "", "El servicio es muy lento.", "The service is very slow."],
      ["limpio", "clean", "🧼", "adjective", "", "La habitación está limpia.", "The room is clean."],
      ["sucio", "dirty", "🧽", "adjective", "", "El suelo está sucio.", "The floor is dirty."],
      ["difícil", "difficult", "🧗", "adjective", "", "El ejercicio es difícil.", "The exercise is difficult."],
      ["fácil", "easy", "✅", "adjective", "", "La pregunta es fácil.", "The question is easy."],
    ],
  },
  {
    theme: "describing", tier: "B1", rows: [
      ["estrecho", "narrow", "↔️", "adjective", "", "La calle es muy estrecha.", "The street is very narrow."],
      ["ancho", "wide", "📐", "adjective", "", "El río es muy ancho aquí.", "The river is very wide here."],
      ["profundo", "deep", "🌊", "adjective", "", "El lago es muy profundo.", "The lake is very deep."],
      ["ligero", "light", "🪶", "adjective", "", "La maleta es ligera.", "The suitcase is light."],
      ["pesado", "heavy", "🏋️", "adjective", "", "Esta caja es muy pesada.", "This box is very heavy."],
      ["suave", "soft", "🧴", "adjective", "", "La tela es muy suave.", "The fabric is very soft."],
      ["áspero", "rough", "🪵", "adjective", "", "La superficie es áspera.", "The surface is rough."],
      ["brillante", "shiny", "✨", "adjective", "", "El metal está brillante.", "The metal is shiny."],
      ["oscuro", "dark", "🌑", "adjective", "", "La habitación está oscura.", "The room is dark."],
      ["transparente", "transparent", "🔍", "adjective", "", "El cristal es transparente.", "The glass is transparent."],
    ],
  },

  // ---- B2 tier: more abstract, nuanced vocabulary for the upper-intermediate
  // path. Authored to stretch beyond concrete A1–B1 nouns into feelings, work,
  // society, and shades of description. -----------------------------------
  {
    theme: "feelings", tier: "B2", rows: [
      ["el agobio", "overwhelm", "😮‍💨", "noun", "m", "Siento un agobio enorme con tanto trabajo.", "I feel huge overwhelm with so much work."],
      ["la incertidumbre", "uncertainty", "🌫️", "noun", "f", "Vivimos una época de incertidumbre.", "We live in a time of uncertainty."],
      ["el resentimiento", "resentment", "💢", "noun", "m", "Guarda resentimiento desde la discusión.", "He holds resentment since the argument."],
      ["el sosiego", "serenity", "🧘", "noun", "m", "Busco un poco de sosiego.", "I seek a little serenity."],
      ["abrumado", "overwhelmed", "🥵", "adjective", "", "Estaba abrumado por las noticias.", "He was overwhelmed by the news."],
      ["resignarse", "to resign oneself", "🤷", "verb", "", "Se resignó a perder el tren.", "She resigned herself to missing the train."],
      ["entrañable", "endearing", "🤗", "adjective", "", "Fue un encuentro entrañable.", "It was a heartwarming encounter."],
      ["la añoranza", "longing", "🕰️", "noun", "f", "Siente añoranza por su pueblo.", "She feels longing for her hometown."],
      ["desbordado", "swamped", "🌊", "adjective", "", "Me siento desbordada esta semana.", "I feel swamped this week."],
      ["conmovedor", "moving", "🥹", "adjective", "", "El discurso fue conmovedor.", "The speech was moving."],
    ],
  },
  {
    theme: "work-study", tier: "B2", rows: [
      ["el cometido", "remit / task", "🎯", "noun", "m", "Cumplió su cometido con eficacia.", "He carried out his remit efficiently."],
      ["el reto", "challenge", "🧗", "noun", "m", "Asumió el reto sin dudar.", "He took on the challenge without hesitating."],
      ["la contratación", "hiring / recruitment", "📝", "noun", "f", "La contratación se congeló este año.", "Hiring was frozen this year."],
      ["el desempeño", "performance", "📈", "noun", "m", "Evalúan el desempeño cada trimestre.", "They assess performance each quarter."],
      ["delegar", "to delegate", "🤝", "verb", "", "Aprendí a delegar tareas.", "I learned to delegate tasks."],
      ["el sindicato", "trade union", "✊", "noun", "m", "El sindicato convocó una reunión.", "The union called a meeting."],
      ["la vacante", "job vacancy", "🪑", "noun", "f", "Hay una vacante en el equipo.", "There is a vacancy in the team."],
      ["el liderazgo", "leadership", "🧭", "noun", "m", "Demostró un gran liderazgo.", "She showed great leadership."],
      ["polivalente", "versatile", "🧰", "adjective", "", "Buscan un perfil polivalente.", "They want a versatile profile."],
      ["la rentabilidad", "profitability", "📈", "noun", "f", "Analizan la rentabilidad del proyecto.", "They analyse the project's profitability."],
    ],
  },
  {
    theme: "town", tier: "B2", rows: [
      ["el concejal", "town councillor", "🧑‍⚖️", "noun", "m", "El concejal presentó el proyecto.", "The councillor presented the project."],
      ["la ordenanza", "bylaw", "📋", "noun", "f", "La ordenanza prohíbe el ruido nocturno.", "The bylaw bans night-time noise."],
      ["la urbe", "metropolis", "🌃", "noun", "f", "La urbe nunca duerme.", "The metropolis never sleeps."],
      ["el extrarradio", "city outskirts", "🏙️", "noun", "m", "Viven en el extrarradio.", "They live on the city outskirts."],
      ["el inmueble", "property", "🏢", "noun", "m", "El inmueble está en venta.", "The building is for sale."],
      ["transitado", "busy (with traffic)", "🚦", "adjective", "", "Es una avenida muy transitada.", "It's a very busy avenue."],
      ["la obra", "roadworks / building site", "🚧", "noun", "f", "Hay obras en toda la avenida.", "There are roadworks along the whole avenue."],
      ["el vecindario", "neighbourhood", "🏡", "noun", "m", "El vecindario es muy tranquilo.", "The neighbourhood is very quiet."],
      ["el alcantarillado", "sewer system", "🚰", "noun", "m", "Renuevan el alcantarillado del barrio.", "They are renovating the neighbourhood's sewer system."],
      ["el trámite", "paperwork / procedure", "📋", "noun", "m", "Hice el trámite en línea.", "I did the procedure online."],
    ],
  },
  {
    theme: "nature", tier: "B2", rows: [
      ["la ladera", "hillside", "⛰️", "noun", "f", "Las casas se alzan en la ladera.", "The houses rise on the hillside."],
      ["el calentamiento", "warming", "🌡️", "noun", "m", "El calentamiento global avanza.", "Global warming is advancing."],
      ["la inundación", "flood", "🌊", "noun", "f", "La inundación arrasó los campos.", "The flood devastated the fields."],
      ["el desarrollo sostenible", "sustainable development", "♻️", "phrase", "", "Apuestan por el desarrollo sostenible.", "They back sustainable development."],
      ["la biodiversidad", "biodiversity", "🦋", "noun", "f", "La biodiversidad está en peligro.", "Biodiversity is under threat."],
      ["talar", "to fell (trees)", "🪓", "verb", "", "Talaron media hectárea de bosque.", "They felled half a hectare of forest."],
      ["el vertido", "spill", "🛢️", "noun", "m", "El vertido dañó la costa.", "The spill damaged the coast."],
      ["renovable", "renewable", "🔋", "adjective", "", "Usamos energía renovable.", "We use renewable energy."],
      ["el residuo", "waste", "🗑️", "noun", "m", "Reciclamos todos los residuos.", "We recycle all the waste."],
      ["preservar", "to preserve", "🌲", "verb", "", "Hay que preservar los bosques.", "We must preserve the forests."],
    ],
  },
  {
    theme: "describing", tier: "B2", rows: [
      ["imprescindible", "essential", "❗", "adjective", "", "Tu ayuda es imprescindible.", "Your help is essential."],
      ["llamativo", "striking", "🌟", "adjective", "", "Lleva un abrigo llamativo.", "She wears a striking coat."],
      ["inusual", "unusual", "❕", "adjective", "", "Fue una reacción inusual.", "It was an unusual reaction."],
      ["fiable", "reliable", "✅", "adjective", "", "Es una fuente fiable.", "It's a reliable source."],
      ["escaso", "scarce", "📉", "adjective", "", "El agua es escasa allí.", "Water is scarce there."],
      ["sorprendente", "surprising", "😲", "adjective", "", "El resultado fue sorprendente.", "The result was surprising."],
      ["duradero", "long-lasting", "⏱️", "adjective", "", "Buscamos una solución duradera.", "We want a long-lasting solution."],
      ["ambiguo", "ambiguous", "❓", "adjective", "", "Su respuesta fue ambigua.", "His answer was ambiguous."],
      ["impactante", "shocking", "💥", "adjective", "", "Las imágenes son impactantes.", "The images are shocking."],
      ["matizado", "nuanced", "🎚️", "adjective", "", "Dio una opinión matizada.", "She gave a nuanced opinion."],
    ],
  },
  // ---- Science & Technology (B2) --------------------------------------
  {
    theme: "science-tech", tier: "B2", rows: [
      ["la investigación", "research", "🔬", "noun", "f", "La investigación duró tres años.", "The research lasted three years."],
      ["el avance", "breakthrough / advance", "🚀", "noun", "m", "Es un avance científico clave.", "It is a key scientific advance."],
      ["el dato", "piece of data", "📊", "noun", "m", "Los datos respaldan la teoría.", "The data support the theory."],
      ["la red", "network", "🕸️", "noun", "f", "La red se cayó esta mañana.", "The network went down this morning."],
      ["el dispositivo", "device", "📱", "noun", "m", "Sincroniza todos tus dispositivos.", "Sync all your devices."],
      ["actualizar", "to update", "🔄", "verb", "", "Tienes que actualizar la aplicación.", "You have to update the app."],
      ["el ensayo", "trial / test", "🧪", "noun", "m", "El ensayo clínico fue exitoso.", "The clinical trial was successful."],
      ["la huella", "footprint / trace", "👣", "noun", "f", "Reducimos la huella de carbono.", "We are cutting the carbon footprint."],
      ["inalámbrico", "wireless", "📶", "adjective", "", "Uso unos auriculares inalámbricos.", "I use wireless headphones."],
      ["el hallazgo", "finding", "💡", "noun", "m", "El hallazgo sorprendió a todos.", "The finding surprised everyone."],
    ],
  },
  // ---- Politics & Society (B2) ----------------------------------------
  {
    theme: "politics", tier: "B2", rows: [
      ["el gobierno", "government", "🏛️", "noun", "m", "El gobierno anunció reformas.", "The government announced reforms."],
      ["la ley", "law", "⚖️", "noun", "f", "La nueva ley entra en vigor mañana.", "The new law takes effect tomorrow."],
      ["el derecho", "right", "✊", "noun", "m", "Es un derecho fundamental.", "It is a fundamental right."],
      ["la ciudadanía", "citizenry", "🧑‍🤝‍🧑", "noun", "f", "La ciudadanía exige transparencia.", "Citizens demand transparency."],
      ["votar", "to vote", "🗳️", "verb", "", "Mañana vamos a votar.", "Tomorrow we are going to vote."],
      ["el partido", "political party", "🚩", "noun", "m", "El partido perdió las elecciones.", "The party lost the election."],
      ["la desigualdad", "inequality", "⚖️", "noun", "f", "Hay mucha desigualdad económica.", "There is a lot of economic inequality."],
      ["el acuerdo", "agreement", "🤝", "noun", "m", "Llegaron a un acuerdo histórico.", "They reached a historic agreement."],
      ["polémico", "controversial", "🔥", "adjective", "", "Fue una decisión polémica.", "It was a controversial decision."],
      ["la manifestación", "protest / demonstration", "📢", "noun", "f", "Miles asistieron a la manifestación.", "Thousands attended the protest."],
    ],
  },
  // ---- Media & News (B2) ----------------------------------------------
  {
    theme: "media", tier: "B2", rows: [
      ["la noticia", "news item", "📰", "noun", "f", "La noticia se difundió rápido.", "The news spread quickly."],
      ["el titular", "headline", "🗞️", "noun", "m", "El titular era exagerado.", "The headline was exaggerated."],
      ["la fuente", "source", "🔎", "noun", "f", "Hay que citar la fuente.", "You have to cite the source."],
      ["la prensa", "the press", "📑", "noun", "f", "La prensa cubrió el evento.", "The press covered the event."],
      ["el reportaje", "report / feature", "🎥", "noun", "m", "Vi un reportaje sobre el tema.", "I saw a feature on the topic."],
      ["difundir", "to spread / broadcast", "📡", "verb", "", "Difundieron el vídeo en redes.", "They spread the video on social media."],
      ["sesgado", "biased", "↔️", "adjective", "", "El artículo está muy sesgado.", "The article is very biased."],
      ["la rueda de prensa", "press conference", "🎙️", "phrase", "", "Convocaron una rueda de prensa.", "They called a press conference."],
      ["el bulo", "hoax / fake news", "🚫", "noun", "m", "Resultó ser un bulo.", "It turned out to be a hoax."],
      ["veraz", "truthful", "✅", "adjective", "", "Busca información veraz.", "Look for truthful information."],
    ],
  },
];

function buildVocabulary() {
  const out = [];
  let rank = 1;
  for (const block of [...THEMES, ...EXTRA_THEME_BLOCKS]) {
    for (const [spanish, english, emoji, pos, gender, exEs, exEn] of block.rows) {
      out.push({
        spanish,
        english,
        emoji,
        theme: block.theme,
        tier: block.tier,
        part_of_speech: pos,
        gender: gender || "none",
        example_es: exEs,
        example_en: exEn,
        rank: rank++,
      });
    }
  }
  return out;
}

// Theme registry — drives the browse view's filter chips and section headers.
export const VOCAB_THEMES = [
  { id: "identity", label: "Identity", emoji: "🪪" },
  { id: "family", label: "Family", emoji: "👪" },
  { id: "house", label: "House & Home", emoji: "🏠" },
  { id: "food", label: "Food & Drink", emoji: "🍽️" },
  { id: "body-health", label: "Body & Health", emoji: "🩺" },
  { id: "daily-routine", label: "Daily Routine", emoji: "⏰" },
  { id: "work-study", label: "Work & Study", emoji: "💼" },
  { id: "travel", label: "Travel", emoji: "🧳" },
  { id: "time-weather", label: "Time & Weather", emoji: "🌤️" },
  { id: "shopping", label: "Shopping", emoji: "🛒" },
  { id: "town", label: "Town & Directions", emoji: "🏙️" },
  { id: "leisure", label: "Leisure", emoji: "🎉" },
  { id: "feelings", label: "Feelings", emoji: "😊" },
  { id: "nature", label: "Nature", emoji: "🌳" },
  { id: "describing", label: "Describing Things", emoji: "🎨" },
  { id: "science-tech", label: "Science & Technology", emoji: "🔬" },
  { id: "politics", label: "Politics & Society", emoji: "🏛️" },
  { id: "media", label: "Media & News", emoji: "📰" },
];

export const VOCAB_TIERS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Flattened, ranked card list — the single source for the view and the seed.
export const VOCABULARY_CARDS = buildVocabulary();
